/**
 * Team + UI palette. Arbitrary placeholders — WP6 renders teams,
 * period. Own-vs-enemy tinting is WP7's problem via a seat-index prop
 * fed to `renderFrame`.
 */

/** Team 0 (cyan). Numeric colors are Pixi's format. */
export const TEAM_0_COLOR = 0x3ec6e0;
/** Team 1 (red-orange). */
export const TEAM_1_COLOR = 0xe0563e;

/** Fallback tint for entities with an unknown team (shouldn't happen). */
export const UNKNOWN_TEAM_COLOR = 0x888888;

/**
 * Viewer-relative projectile tints (#467). Unlike `TEAM_0_COLOR`/`TEAM_1_COLOR`
 * (fixed by team INDEX), these are picked by comparing a projectile's
 * `ownerTeam` against the LOCAL VIEWER's own team — "enemy" and "own team"
 * are relative to whoever is watching, not a fixed team index. Both are a
 * flat 20%-toward-white lighten of the corresponding solid champion-body
 * color (`TEAM_0_COLOR` → own/blue-family, `TEAM_1_COLOR` → enemy/red-family)
 * so a projectile reads as related-but-distinct from the solid body color,
 * never identical to it.
 */
/** Own-team projectile fill — lightened `TEAM_0_COLOR` (blue-ish). */
export const OWN_PROJECTILE_COLOR = 0x65d1e6;
/** Enemy projectile fill — lightened `TEAM_1_COLOR` (red-orange-ish). */
export const ENEMY_PROJECTILE_COLOR = 0xe67865;

/** Background clear color. */
export const BG_COLOR = 0x0b0f14;

/** Arena border line color. */
export const ARENA_BORDER_COLOR = 0x2a3947;

/** Spawn marker color. */
export const SPAWN_MARKER_COLOR = 0x4a6a80;

// ─── Arena / map art (#172, bible §7 — world palette, ≤35% sat) ──────────────

/** Pillar side face — dark stone silhouette ("blocks movement/LoS" read). */
export const PILLAR_SIDE_COLOR = 0x2b2823;

/** Pillar top face — mid stone, catches the light. */
export const PILLAR_TOP_COLOR = 0x4a443b;

/** Pillar rim light — sand accent stroke on the top edge (bible §7). */
export const PILLAR_RIM_COLOR = 0x8c7e68;

/** Pillar ground-shadow ring — drawn dark + semi-transparent at the footprint
 *  so the collision circle reads on the floor (bible §7 "footprint accuracy"). */
export const PILLAR_SHADOW_COLOR = 0x000000;

/** Edge wall band — quiet stone low-wall replacing the flat 1 px border. */
export const EDGE_WALL_COLOR = 0x3a3630;

/**
 * HP bar fill (#344) — ALWAYS this one green, regardless of hp fraction. The
 * bar's WIDTH already conveys the fraction; the old green→red gradient by
 * fraction was redundant (and made a low-resource bar read like a low-health
 * one at a glance, once the resource bar landed alongside it, #337). DOM
 * frames use the CSS twin `HP_BAR_FILL_COLOR` in `ui/hud-util.ts` — KEEP
 * THESE TWO VALUES IN SYNC (#681): same green (`#3aa675`), just the Pixi
 * numeric form, so the in-world nameplate bar and the DOM frame bars read as
 * the same color even though they're on opposite sides of the DOM/Pixi
 * rendering-technology split.
 */
export const HP_BAR_COLOR = 0x3aa675;

/** HP bar background (empty portion). */
export const HP_BAR_BG = 0x161d24;

/** HP / cast bar 1 px border. */
export const BAR_BORDER = 0x000000;

/** Cast bar fill. */
export const CAST_BAR = 0xf1c40f;

/** Body outline. */
export const BODY_OUTLINE = 0x0a1520;

/** Facing tick line. */
export const FACING_TICK = 0xf0f0f0;

/**
 * Dynamic-terrain wall fill (#488) — placeholder plain white per the original
 * ask (not an art pass; see `entityView.ts`'s `renderWalls`). A real
 * ice-themed texture is a future follow-up, same rip-out convention the
 * arena art (#172) uses for pillars/floor/edge.
 */
export const WALL_COLOR = 0xffffff;

/**
 * Return the team color for a team index (0 or 1).
 * Any other team index gets the unknown fallback — the wire schema
 * enforces the range, so this is defensive not load-bearing.
 */
export function teamColor(team: number): number {
  if (team === 0) return TEAM_0_COLOR;
  if (team === 1) return TEAM_1_COLOR;
  return UNKNOWN_TEAM_COLOR;
}


